import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { articlesApi, ArticleWithBody } from '../../services/api/adapter';
import './Article.css';

export const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleWithBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    articlesApi.getArticleBySlug(slug)
      .then(data => {
        setArticle(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [slug, navigate]);

  // Set page meta tags
  useEffect(() => {
    if (!article) return;
    const title = article.title + ' | Practical Media';
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const desc = article.description || article.title;
    setMeta('description', desc);
    setMeta('og:title', article.title);
    setMeta('og:description', desc);
    setMeta('og:type', 'article');
    setMeta('og:url', window.location.href);
    if (article.cover_image) setMeta('og:image', article.cover_image);

    return () => {
      document.title = 'Practical Media';
      setMeta('description', 'Practical Media - Developer-first game publishing. Insights, guides, and analysis for indie game developers.');
    };
  }, [article]);

  if (loading) return <div className="container page"><p style={{color: 'var(--color-text-muted)'}}>Loading...</p></div>;

  if (error || !article) return (
    <div className="container page">
      <p style={{color: 'var(--color-text-muted)'}}>Article not found.</p>
      <Link to="/blog" className="accent-link" style={{marginTop: 12, display: 'inline-block'}}>&larr; back to articles</Link>
    </div>
  );

  const tags = typeof article.tags === 'string' ? JSON.parse(article.tags) : (article.tags || []);
  const cleanTags = tags.filter((t: string) => t && !t.startsWith('[') && t.length < 40);

  return (
    <div className="container page">
      <article>
        <header className="article-header">
          <Link to="/blog" className="back-link">&larr; articles</Link>
          <h1 className="article-title">{article.title}</h1>
          <div className="article-meta">
            <span>{new Date(article.uploadDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            {cleanTags.length > 0 && <span className="article-tags-meta">{cleanTags.join(', ')}</span>}
          </div>
        </header>
        <div className="article-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ node, ...props }) => <a {...props} target="_blank" rel="nofollow noopener noreferrer" />
            }}
          >
            {article.content || article.body}
          </ReactMarkdown>
        </div>

        {cleanTags.length > 0 && (
          <footer className="article-footer">
            <p className="label">Tags</p>
            <div className="article-tag-list">
              {cleanTags.map((tag: string) => (
                <Link key={tag} to={`/blog?tags=${tag}`} className="article-tag">{tag}</Link>
              ))}
            </div>
          </footer>
        )}
      </article>
    </div>
  );
};
